import React from 'react';
import { logApplicationError } from '@/utils/safeErrorMessage';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/** Best-effort name from a single React element child */
const getChildDisplayName = (children) => {
	try {
		const child = React.Children.only(children);
		if (!React.isValidElement(child)) return null;
		const type = child.type;
		if (typeof type === 'string') return type; // 'div', etc.
		return type.displayName || type.name || null;
	} catch {
		return null;
	}
};

export class FolioErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		const childDisplayName = getChildDisplayName(this.props.children);

		logApplicationError(this.props.context ?? 'React render error', {
			message: error?.message,
			stack: error?.stack,
			componentStack: errorInfo?.componentStack,
			boundary: this.props.name ?? 'FolioErrorBoundary',
			dealId: this.props.dealId ?? null,
			clientId: this.props.clientId ?? null,
			childDisplayName, // direct child only
			scope: this.props.scope ?? '', // your explicit label, e.g. tab slug
			pathname: window.location.pathname,
			extra: this.props.extra ?? null,
			instantSearchState: window.__FOLIO_INSTANT_SEARCH_STATE__ ?? null,
		});
	}

	handleReset = () => {
		this.setState({ hasError: false });
	};

	render() {
		if (this.state.hasError) {
			return (
				this.props.fallback ?? (
					<Stack spacing={1} direction='row' sx={{ alignItems: 'center' }}>
						<Typography variant='task_updated' color='text.secondary'>
							{this.props.message ?? 'Something went wrong.'}
						</Typography>
						<Box>
							<IconButton onClick={this.handleReset} size='small' color='primary'>
								<RefreshIcon sx={{ fontSize: '16px' }} />
							</IconButton>
						</Box>
					</Stack>
				)
			);
		}

		return this.props.children;
	}
}
